import { styled, Typography } from '@mui/material';

import { Tooltip } from '../../atoms/Tooltip';
import {
  type SyncStatus,
  syncStatusColor,
  type SyncStatusIndicatorProps,
  useSyncStatusIndicator,
} from './useSyncStatusIndicator';

type Props = SyncStatusIndicatorProps;

const DOT_SIZE = 8;
const HALO_OPACITY = 0.2;

const Root = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const Dot = styled('span')<{ ownerState: { status: SyncStatus; isLive: boolean } }>(({ theme, ownerState }) => {
  const color = syncStatusColor(theme, ownerState.status);

  return {
    width: DOT_SIZE,
    height: DOT_SIZE,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: color,
    ...(ownerState.isLive && {
      boxShadow: `0 0 0 3px ${theme.alpha(color, HALO_OPACITY)}`,
    }),
  };
});

/**
 * Passive freshness indicator for a background synchronisation (mailbox, calendar, drive).
 * The colored dot always comes with an explicit label (Color-With-Label Rule).
 */
export function SyncStatusIndicator(props: Props) {
  const hook = useSyncStatusIndicator(props);

  const content = (
    <Root
      data-testid={props['data-testid'] ?? 'sync-status-indicator'}
      data-status={props.status}
      tabIndex={hook.hasTooltip ? 0 : undefined}
    >
      <Dot ownerState={{ status: props.status, isLive: hook.isLive }} aria-hidden="true" />
      <Typography variant="body2" component="span" color="inherit">
        {props.label}
      </Typography>
    </Root>
  );

  if (!props.tooltip) {
    return content;
  }

  return (
    <Tooltip title={props.tooltip} describeChild>
      {content}
    </Tooltip>
  );
}

export type { Props as SyncStatusIndicatorProps };
